import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Truck, Shield, RefreshCcw, Star, ChevronRight, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { ProductCard } from '../components/ProductCard';
import { RecommendedProducts } from '../components/RecommendedProducts';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [flashProducts, setFlashProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [productsRes, categoriesRes, testimonialsRes, flashRes] = await Promise.all([
          axios.get(`${API}/products/featured?limit=8`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/testimonials?limit=6&lang=${language}`).catch(() => ({ data: [] })),
          axios.get(`${API}/products/flash-sales?limit=8`).catch(() => ({ data: [] })),
        ]);
        
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setTestimonials(testimonialsRes.data || []);
        setFlashProducts(flashRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const features = [
    {
      icon: Truck,
      title: t('freeShippingShort'),
      description: t('onOrdersOver')
    },
    {
      icon: Shield,
      title: t('securePayment'),
      description: t('securePaymentDesc')
    },
    {
      icon: RefreshCcw,
      title: t('easyReturns'),
      description: t('easyReturnsDesc')
    },
    {
      icon: Star,
      title: t('verifiedSellers'),
      description: t('trustedPartners')
    }
  ];

  const categoryImages = {
    electronics: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?auto=format&fit=crop&w=800&q=80',
    fashion: 'https://images.unsplash.com/photo-1572015814739-6624032ea483?crop=entropy&cs=srgb&fm=jpg&q=85',
    'home-garden': 'https://images.unsplash.com/photo-1760072513442-9872656c1b07?crop=entropy&cs=srgb&fm=jpg&q=85',
    beauty: 'https://images.unsplash.com/photo-1596462502278-27bfdd403348?auto=format&fit=crop&w=800&q=80',
    sports: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?auto=format&fit=crop&w=800&q=80',
    books: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=800&q=80'
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title={language === 'fr' ? 'Marketplace responsable — Marques, revendeurs & particuliers' : 'The conscious marketplace — Brands, resellers & individuals'}
        description={language === 'fr'
          ? 'Respectful Markets : achetez et vendez auprès de marques, revendeurs et particuliers vérifiés. Multi-transporteurs, paiement sécurisé Stripe, programme d\'affiliation.'
          : 'Respectful Markets: buy and sell with verified brands, resellers and individuals. Multi-carrier shipping, secure Stripe payment, affiliate program.'}
        url={window.location.origin}
      />
      {/* Hero Section */}
      <section className="hero-gradient noise-overlay relative overflow-hidden">
        <div className="container-main py-16 md:py-24 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white animate-slide-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                {t('heroTitle')}
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-8 max-w-lg">
                {t('heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 h-12 px-8 rounded-full font-bold text-lg"
                  onClick={() => navigate('/shop')}
                  data-testid="shop-now-btn"
                >
                  {t('shopNow')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 h-12 px-8 rounded-full font-semibold"
                  onClick={() => navigate('/become-seller')}
                  data-testid="become-seller-btn"
                >
                  {t('becomeASeller')}
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1607082350899-7e105aa886ae?auto=format&fit=crop&w=800&q=80"
                  alt="Shopping"
                  className="rounded-2xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-[#E9C46A] fill-[#E9C46A]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#0F4C5C]">10,000+</p>
                      <p className="text-sm text-muted-foreground">
                        {t('verifiedSellers')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-white border-b border-border/40">
        <div className="container-main py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0F4C5C]/5 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-[#0F4C5C]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flash Sales */}
      {flashProducts.length > 0 && (
        <section className="container-main py-16" data-testid="flash-sales-section">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-red-600 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white fill-white" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-red-600">
                  {language === 'fr' ? 'Ventes Flash' : 'Flash Sales'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Offres à durée limitée — faites vite !' : 'Limited-time deals — be quick!'}
                </p>
              </div>
            </div>
            <Link to="/shop?flash=1" className="text-red-600 hover:text-red-700 font-medium flex items-center gap-1" data-testid="flash-sales-view-all">
              {t('viewAll')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="product-grid">
            {flashProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* Personalized recommendations */}
      <RecommendedProducts limit={8} />

      {/* Categories Bento Grid */}
      <section className="container-main py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-bold">{t('exploreCategories')}</h2>
          <Link to="/shop" className="text-[#0F4C5C] hover:text-[#2A9D8F] font-medium flex items-center gap-1">
            {t('viewAll')}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        {loading ? (
          <div className="bento-grid">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[180px]">
            {categories.slice(0, 6).map((category, index) => {
              // Make first and third items larger
              const isLarge = index === 0 || index === 2;
              return (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className={`relative rounded-2xl overflow-hidden group ${
                    isLarge ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image_url || categoryImages[category.slug] || 'https://via.placeholder.com/400'}
                    alt={getLocalizedName(category)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className={`font-bold text-white ${isLarge ? 'text-2xl' : 'text-lg'}`}>
                      {getLocalizedName(category)}
                    </h3>
                    <p className="text-white/70 text-sm mt-1 flex items-center gap-1 group-hover:text-[#E9C46A] transition-colors">
                      {t('shopNow')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Featured Products */}
      <section className="bg-slate-50 py-16">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold">{t('featuredProducts')}</h2>
            <Link to="/shop" className="text-[#0F4C5C] hover:text-[#2A9D8F] font-medium flex items-center gap-1">
              {t('viewAll')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="product-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-square rounded-xl" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="product-grid">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section — Real customer reviews */}
      {testimonials.length > 0 && (
        <section className="container-main py-16" data-testid="testimonials-section">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#E9C46A]/15 text-[#0F4C5C] rounded-full mb-3">
              {language === 'fr' ? 'Témoignages' : 'Testimonials'}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              {language === 'fr' ? 'Ce que disent nos clients' : 'What our customers say'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'fr'
                ? 'De vrais avis vérifiés de notre communauté de plus de 250 000 acheteurs.'
                : 'Real verified reviews from our community of over 250,000 buyers.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((tst) => (
              <div
                key={tst.id}
                className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`testimonial-card-${tst.id}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(tst.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#E9C46A] text-[#E9C46A]" />
                  ))}
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed mb-4 line-clamp-5">
                  &ldquo;{tst.comment}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-border/40">
                  {tst.product_image && (
                    <img
                      src={tst.product_image}
                      alt={tst.product_title}
                      className="w-10 h-10 rounded-lg object-cover bg-slate-100"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{tst.user_name}</p>
                    {tst.product_title && (
                      <p className="text-xs text-muted-foreground truncate">
                        {language === 'fr' ? 'a acheté' : 'bought'} {tst.product_title}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA Section — Rejoindre notre communauté (synced with /become-seller) */}
      <section className="container-main py-16" data-testid="prefooter-cta">
        <div className="bg-[#0F4C5C] rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-5 items-stretch">
            <div className="lg:col-span-3 p-8 md:p-12 text-white">
              <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#E9C46A]/15 text-[#E9C46A] rounded-full mb-3">
                {language === 'fr' ? 'Rejoindre notre communauté' : 'Join our community'}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                {language === 'fr' ? 'Devenez Vendeur' : 'Become a Seller'}
              </h2>
              <p className="text-white/80 mb-6 max-w-xl">
                {language === 'fr'
                  ? 'Rejoignez Respectful Markets et développez votre activité avec notre plateforme de confiance.'
                  : 'Join Respectful Markets and grow your business with our trusted marketplace.'}
              </p>

              {/* 4 benefits */}
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-7">
                {[
                  { fr: ['Paiements Rapides', 'Via Stripe Connect sécurisé'], en: ['Fast Payments', 'Via secure Stripe Connect'] },
                  { fr: ['Tableau de Bord', 'Gérez vos produits facilement'], en: ['Dashboard', 'Manage products easily'] },
                  { fr: ['Support Multilingue', 'Vendez dans 7 langues européennes'], en: ['Multilingual Support', 'Sell in 7 European languages'] },
                  { fr: ['Devis B2B intégrés', 'Acceptez et payez via Stripe'], en: ['B2B Quotes integrated', 'Accept and pay via Stripe'] },
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 mt-0.5 rounded-full bg-[#2A9D8F] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="leading-tight">
                      <p className="font-semibold text-white text-sm">{language === 'fr' ? b.fr[0] : b.en[0]}</p>
                      <p className="text-white/65 text-xs">{language === 'fr' ? b.fr[1] : b.en[1]}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* How it works — 3 steps */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-[#E9C46A] mb-3">
                  {language === 'fr' ? 'Comment ça marche ?' : 'How it works?'}
                </p>
                <ol className="grid sm:grid-cols-3 gap-3 text-sm">
                  {[
                    { fr: 'Remplissez le formulaire d\'inscription', en: 'Fill out the registration form' },
                    { fr: 'Notre équipe valide votre profil', en: 'Our team validates your profile' },
                    { fr: 'Ajoutez vos produits et vendez !', en: 'Add your products and start selling!' },
                  ].map((step, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="w-6 h-6 bg-[#E9C46A] text-[#0F4C5C] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                      <span className="text-white/85 text-xs leading-snug">{language === 'fr' ? step.fr : step.en}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 h-12 px-7 rounded-full font-bold"
                  onClick={() => navigate('/become-seller')}
                  data-testid="prefooter-become-seller-btn"
                >
                  {t('becomeASeller')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="bg-transparent border-white/30 text-white hover:bg-white/10 h-12 px-7 rounded-full font-bold"
                  onClick={() => navigate('/pricing')}
                  data-testid="prefooter-pricing-btn"
                >
                  {language === 'fr' ? 'Voir nos tarifs' : 'See pricing'}
                </Button>
              </div>
            </div>

            {/* Right column — pricing recap (Starter + Pro) */}
            <div className="lg:col-span-2 bg-[#0B3A47] p-6 md:p-8 flex flex-col gap-4 border-t lg:border-t-0 lg:border-l border-white/10">
              <div className="flex items-baseline justify-between">
                <p className="font-bold text-white text-base">
                  {language === 'fr' ? 'Nos formules' : 'Our plans'}
                </p>
                <span className="text-xs text-[#E9C46A] font-semibold">
                  {language === 'fr' ? 'Sans frais cachés' : 'No hidden fees'}
                </span>
              </div>

              {/* Starter */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-white">Starter</p>
                  <span className="text-xs px-2 py-0.5 bg-[#2A9D8F]/20 text-[#2A9D8F] rounded-full font-semibold">
                    {language === 'fr' ? 'Gratuit' : 'Free'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-white">
                  0€<span className="text-xs text-white/50 font-normal">/{language === 'fr' ? 'mois' : 'mo'}</span>
                </p>
                <p className="text-xs text-white/60 mt-0.5">
                  {language === 'fr' ? '12% de commission par vente' : '12% commission per sale'}
                </p>
              </div>

              {/* Pro - featured */}
              <div className="bg-[#E9C46A] text-[#0F4C5C] rounded-xl p-4 relative">
                <span className="absolute -top-2 right-3 bg-[#0F4C5C] text-[#E9C46A] text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
                  {language === 'fr' ? 'Le + populaire' : 'Most popular'}
                </span>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold">Pro</p>
                  <span className="text-xs px-2 py-0.5 bg-[#0F4C5C] text-[#E9C46A] rounded-full font-semibold">
                    {language === 'fr' ? 'Seulement 3%' : 'Just 3%'}
                  </span>
                </div>
                <p className="text-2xl font-bold">
                  25€<span className="text-xs font-normal">/{language === 'fr' ? 'mois' : 'mo'}</span>
                </p>
                <p className="text-xs mt-0.5 opacity-80">
                  {language === 'fr' ? 'Produits illimités · Analytics · Devis B2B' : 'Unlimited products · Analytics · B2B quotes'}
                </p>
              </div>

              <p className="text-[11px] text-white/50 text-center mt-1">
                {language === 'fr' ? 'Paiements hebdomadaires via Stripe Connect' : 'Weekly payouts via Stripe Connect'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
